import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnimatedSkeletonLoader } from '@usepepper/ui/atoms/animated-skeleton-loader';
import { AvailabilityConstraints } from '@/experiences/sales-portal/domain/ordering/blocs/loaders/use-sales-portal-available-order-fulfillments-loader';
import {
  cancelIdleCallbackSafe,
  requestIdleCallbackSafe,
} from '@/utils/idle-callback';
import { CatalogSearchEmptyState } from '@/experiences/sales-portal/components/catalog-search-empty-state';
import {
  CatalogSearchErrorCode,
  selectIsLoading as selectCatalogIsLoading,
  selectShowEmptyState as selectCatalogShowEmptyState,
  selectShowError as selectCatalogShowError,
  selectShowResults as selectCatalogShowResults,
  selectIsCatalogMode,
  useCatalogSearchStore,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/catalog-search-store';
import { CatalogSearchTable } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/catalog-search-table/catalog-search-table';
import { CheckoutPanel } from '@/experiences/sales-portal/domain/ordering/containers/checkout-panel/checkout-panel';
import { ColumnId } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import {
  ConfigurationDomain,
  TableName,
} from '@/domain/configuration/blocs/stores/column-preferences-keys';
import { ConfigureColumnsButton } from '@experiences/components/table/configure-columns-button';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_EASING_OUT_CONFIG } from '@experiences/blocs/utils/animations';
import { EmployeeRole, GetAvailableOrderFulfillmentResponse } from '@/types';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { getOrderEntryConfigurableColumns } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/utils/configurable-columns-utils';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import { LayoutChangeEvent, Platform, StyleSheet, View } from 'react-native';
import { Moment } from 'moment';
import { OrderEntryEmptyState } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-empty-state';
import { OrderEntryMode } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/types';
import { OrderEntryReviewEmptyState } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-review-empty-state';
import { OrderEntryReviewSearchEmptyState } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-review-search-empty-state';
import { OrderEntrySearchEmptyState } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-search-empty-state';
import {
  OrderEntrySearchInput,
  OrderEntrySearchInputRef,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-search-input';
import { OrderEntryTable } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table';
import { OrderEntryTableSkeleton } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/order-entry-table-skeleton';
import {
  OrderGuideCollectionData,
  useSalesPortalOrderGuideCollectionsLoader,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/sales-portal-order-guide-collections-loader';
import { OrderGuideSelector } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-guide-selector';
import { OrderSummaryBottomBar } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/containers/order-summary-bottom-bar';
import {
  PermissionAction,
  PermissionSubject,
  SalesPortalOrderEntryAction,
  SalesRepOverrideMode,
} from '@/enums';
import { RestaurantListData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/sales-portal-restaurant-transformer';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import {
  SalesPortalKeyboardScope,
  SalesPortalShortcuts,
} from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { SalesPortalStackParams } from '@/navigators/types';
import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import { SearchCatalogFooter } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/search-catalog-footer';
import {
  selectSalesPortalCartItems,
  selectSalesPortalCartOrderId,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { shiftTabDebounce } from '@experiences/components/table/blocs/hooks/use-focus-skip-detection';
import { SideDrawerRef } from '@experiences/components/side-drawer';
import { SortingState, TableState } from '@tanstack/react-table';
import { StackNavigationProp } from '@react-navigation/stack';
import { TableScrollHandle } from '@experiences/components/table/types';
import {
  TimeslotOption,
  TimeZoneMap,
} from '@domain/fulfillment-settings/types/types';
import { useAuth } from '@/contexts/auth-context';
import { useCanWriteRequestNote } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-can-write-request-note';
import { useCatalogSearchLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-catalog-search-loader';
import { useConfidentialVisibilityStore } from '@/experiences/sales-portal/domain/ordering/blocs/stores/order-entry-confidential-visibility-store';
import { useDebouncedCallback } from 'use-debounce';
import { useEmployeePermissionsLoader } from '@/domain/access-control/blocs/loaders/use-employee-permissions-loader';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useInputKeyOverride } from '@/experiences/sales-portal/hooks/use-input-key-override';
import { useIsGrossProfitHidden } from '@/experiences/sales-portal/blocs/hooks/use-is-gross-profit-hidden';
import { useKeyboardHotkeysContext } from '@/contexts/keyboard-shortcuts/keyboard-shortcuts-context';
import { useLastPurchaseInfoLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-last-purchase-info-loader';
import { useOrderEntryCommissionLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-order-entry-commission-loader';
import { useOrderEntryDataLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-order-entry-data-loader';
import { usePriceEditSave } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-price-edit-save';
import { useRequestNoteMutator } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/mutators/use-request-note-mutator';
import { useSalesPortalOrderEntryReviewDataLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-sales-portal-order-entry-review-data-loader';
import { useSalesRepConfig } from '@/experiences/sales-portal/blocs/hooks/use-sales-rep-config';
import { useSaveSalesPortalCartMutator } from '@/experiences/sales-portal/domain/ordering/blocs/mutators/use-save-sales-portal-cart-mutator';
import { useScreenScopedKeyboardShortcuts } from '@experiences/platform/shortcuts/use-screen-scoped-keyboard-shortcuts';
import { useShallow } from 'zustand/react/shallow';
import { useTableFocusStore } from '@experiences/components/table/blocs/stores/table-focus-store';
import type { OrderEntryTableRowData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';

const SEARCH_DEBOUNCE_MS = 150;
const CATALOG_SEARCH_DEBOUNCE_MS = 500;
const SELECTOR_MAX_WIDTH = 176;
// PERF: Stable reference so React.memo on the inactive table isn't
// defeated by a new {} each render.
const EMPTY_TABLE_STATE: Partial<TableState> = {};
// Default sort for the review tab: first added item at top
const REVIEW_DEFAULT_SORTING: SortingState = [
  { desc: false, id: ColumnId.AddedAt },
];

// TODO(DSR-3504): fulfillments logic should be moved into BLoC
interface OrderEntryScreenContentProps {
  action?: SalesPortalOrderEntryAction;
  additionalOrderValidationRoles: EmployeeRole[];
  areSubstitutionsEnabled: boolean;
  availabilityConstraints: AvailabilityConstraints | null;
  availableFulfillments: GetAvailableOrderFulfillmentResponse[];
  employeeRole: EmployeeRole | null;
  getTimeslotOptionsForDate: (date: Moment) => TimeslotOption[];
  handleSelectedFulfillment: (fulfillment: SavedSelectedFulfillment) => void;
  isPriceEditingExperimentEnabled: boolean;
  /**
   * When false, item min/max order quantities are ignored for the current
   * user — the supplier has exempted admins via
   * order_quantity_config.enforce_for_supplier_admins.
   */
  minMaxQuantityEnabled: boolean;
  onFocusedRowChange?: (rowData: OrderEntryTableRowData | null) => void;
  onOpenRestaurantSelector?: () => void;
  /**
   * Forwarded to the build, review, and catalog tables so a row press opens
   * the item-detail side panel — the panel itself is owned by the parent
   * screen.
   */
  onRowPress?: (rowData: OrderEntryTableRowData) => void;
  refetchFulfillments: () => void;
  restaurantUUID: string;
  savedSelectedFulfillment: SavedSelectedFulfillment;
  selectableFulfillmentOptions: GetAvailableOrderFulfillmentResponse[];
  selectedRestaurant: RestaurantListData;
  supplierUUID: string;
  timezones: TimeZoneMap;
}

export const OrderEntryScreenContent: React.FC<
  OrderEntryScreenContentProps
> = ({
  action,
  additionalOrderValidationRoles,
  areSubstitutionsEnabled,
  availabilityConstraints,
  availableFulfillments,
  selectableFulfillmentOptions,
  isPriceEditingExperimentEnabled,
  getTimeslotOptionsForDate,
  handleSelectedFulfillment,
  minMaxQuantityEnabled,
  onFocusedRowChange,
  onOpenRestaurantSelector,
  onRowPress,
  refetchFulfillments,
  restaurantUUID,
  savedSelectedFulfillment,
  selectedRestaurant,
  employeeRole,
  supplierUUID,
  timezones,
}) => {
  const toggleConfidentialFieldDisplay = useConfidentialVisibilityStore(
    (state) => state.toggleConfidentialFieldDisplay,
  );

  const [styles, theme] = useStyles();
  const navigation =
    useNavigation<StackNavigationProp<SalesPortalStackParams>>();

  const { disableScope, enableScope } = useKeyboardHotkeysContext();
  const authenticatedUserUUID = useAuth((s) => s.authenticatedUserUUID);
  const closeItemDetailPanel = useCallback(() => {
    navigation.setParams({
      itemDetailTab: undefined,
      variantPackUUID: undefined,
    });
  }, [navigation]);

  // Price editing: fetch sales rep config and permissions
  const { salesRepConfig, loading: salesRepConfigLoading } = useSalesRepConfig(
    supplierUUID,
    selectedRestaurant.chatUUID,
  );
  const { can } = useEmployeePermissionsLoader(
    supplierUUID,
    authenticatedUserUUID,
  );

  // Pricing cells render as read-only text when: supplier/chat is not in experiment,
  // user is missing srt_price_editing WRITE permission, or supplier/chat has price
  // overrides disabled.
  // Gate on salesRepConfig loading so price cells default to read-only (and
  // commissions stay hidden) until the config resolves, rather than briefly
  // deriving editability from a not-yet-loaded config.
  const isPriceEditingEnabled =
    !salesRepConfigLoading &&
    isPriceEditingExperimentEnabled &&
    can(PermissionAction.Write, PermissionSubject.SrtPriceEditing) &&
    salesRepConfig?.override_mode !== SalesRepOverrideMode.DISABLED &&
    !salesRepConfig?.disable_override_editing;

  // Commission display requires allow_commission employee permission + supplier-level enablement
  const isCommissionsEnabled =
    !salesRepConfigLoading &&
    can(PermissionAction.Read, PermissionSubject.Commissions) &&
    (salesRepConfig?.commission_settings?.draft_order_commission_enabled ??
      false);

  // Override indicators are visible for READ or WRITE permission holders
  const showPriceOverrideInfo =
    isPriceEditingExperimentEnabled &&
    can(PermissionAction.Read, PermissionSubject.SrtPriceEditing);

  const canSetManualOverrideExpiration =
    showPriceOverrideInfo &&
    !!salesRepConfig?.can_set_manual_override_expiration;

  // GP% and GP$ columns/metrics are hidden when the ops config flag is enabled.
  const isGrossProfitHidden = useIsGrossProfitHidden(supplierUUID);

  const currencyCode = selectedRestaurant.currencyCode;

  // Request notes: check if enabled via dynamic config and user has write access
  const canWriteRequestNote = useCanWriteRequestNote(
    supplierUUID,
    selectedRestaurant.chatUUID,
  );

  // Order entry summary + controls
  const [orderEntryMode, setOrderEntryMode] = useState<OrderEntryMode>(
    OrderEntryMode.Build,
  );
  // PERF: useDeferredValue defers the mode value passed to tables so the
  // segmented control indicator can animate immediately on the UI thread.
  const deferredOrderEntryMode = useDeferredValue(orderEntryMode);

  const [bottomBarHeight, setBottomBarHeight] = useState<number>(0);

  const onBottomBarLayout = useCallback((event: LayoutChangeEvent) => {
    setBottomBarHeight(event.nativeEvent.layout.height);
  }, []);

  // PERF: searchText lives inside OrderEntrySearchInput (uncontrolled).
  // The parent only stores the debounced value to avoid re-rendering on every keystroke.
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  // Note: intentionally NOT wrapped in startTransition — the debounce already
  // batches keystrokes, and startTransition can be starved by cart updates.
  const debouncedSetSearchText = useDebouncedCallback((text: string) => {
    setDebouncedSearchText(text);
  }, SEARCH_DEBOUNCE_MS);
  const searchInputRef = useRef<OrderEntrySearchInputRef>(null);
  const catalogScrollHandleRef = useRef<TableScrollHandle | null>(null);

  // Catalog search store state
  const isInCatalogMode = useCatalogSearchStore(selectIsCatalogMode);
  const isCatalogLoading = useCatalogSearchStore(selectCatalogIsLoading);
  const showCatalogResults = useCatalogSearchStore(selectCatalogShowResults);
  const showCatalogEmptyState = useCatalogSearchStore(
    selectCatalogShowEmptyState,
  );
  const showCatalogError = useCatalogSearchStore(selectCatalogShowError);
  const catalogSearchTerm = useCatalogSearchStore((state) => state.searchTerm);
  const catalogResults = useCatalogSearchStore((state) => state.results);
  const onExitCatalogMode = useCatalogSearchStore(
    (state) => state.onExitCatalogMode,
  );

  // Callback to focus search input - used by keyboard shortcut and quantity cells
  const handleTriggerSearch = useCallback(() => {
    searchInputRef.current?.focus();
  }, []);

  // Keyboard shortcut: / to focus search input
  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.Search,
    handleTriggerSearch,
    [handleTriggerSearch],
    {},
    true,
    SalesPortalKeyboardScope.ORDER_ENTRY_TABLE,
  );

  // Keyboard shortcut: D to toggle confidential field visibility (sales-rep "in
  // front of operator" mode). Scoped to the table so it won't fire while the
  // restaurant selector or checkout panel owns focus.
  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.ToggleConfidentialFields,
    toggleConfidentialFieldDisplay,
    [toggleConfidentialFieldDisplay],
    {},
    true,
    SalesPortalKeyboardScope.ORDER_ENTRY_TABLE,
  );

  const debouncedResetLastFocused = useDebouncedCallback(() => {
    useTableFocusStore.getState().resetLastFocusedIndexForAllTables();
  }, SEARCH_DEBOUNCE_MS);

  // Clear table focus when search input gains focus
  // This prevents the flash when pressing down arrow from search.
  const handleSearchFocus = useCallback(() => {
    // iPad: UIKit pre-empts Shift+Tab from a cell and can land OS focus on
    // this search input when the previous editable cell isn't visible.
    // Bounce so the table's pending moveFocusLeft + auto-focus can take over.
    if (Platform.OS === 'ios' && shiftTabDebounce.isActive()) {
      searchInputRef.current?.blur();
      return;
    }
    const { activeTableId, setFocusedIndex } = useTableFocusStore.getState();
    if (activeTableId) {
      setFocusedIndex(activeTableId, null);
    }
  }, []);

  // Override down arrow in search input to focus table row (last focused, or 0).
  const handleSearchDownArrow = useCallback(() => {
    const {
      activeTableId,
      lastFocusedIndexByTableId,
      setFocusedIndex,
      totalRowsByTableId,
    } = useTableFocusStore.getState();

    if (!activeTableId) {
      return;
    }

    const totalRows = totalRowsByTableId[activeTableId] ?? 0;
    if (totalRows === 0) {
      return;
    }

    const lastFocused = lastFocusedIndexByTableId[activeTableId] ?? 0;
    const indexToFocus = Math.min(lastFocused, totalRows - 1);

    setFocusedIndex(activeTableId, indexToFocus);
  }, []);

  const { handleKeyPress: handleSearchKeyPress } = useInputKeyOverride({
    enabled: true,
    key: Key.ArrowDown,
    onKeyPress: handleSearchDownArrow,
  });

  // Fulfillment settings
  const supplierTimezone = selectedRestaurant.supplierTimezone;
  const deliveryDate = savedSelectedFulfillment.date;

  const { showErrorToast } = useExperienceToasts();

  const { handleSave: handleSavePriceEditBase } = usePriceEditSave({
    deliveryDate,
    onSaveError: showErrorToast,
    overrideMode:
      salesRepConfig?.override_mode ?? SalesRepOverrideMode.DISABLED,
    restaurantUUID,
    salesRepConfig,
    supplierUUID,
  });

  const { commitNote: handleSaveRequestNote } = useRequestNoteMutator({
    onSaveError: showErrorToast,
    restaurantUUID,
    supplierUUID,
  });

  // Show error toast when catalog search fails (callback from loader, not useEffect)
  const handleCatalogSearchError = useCallback(
    (code: CatalogSearchErrorCode) => {
      const message =
        code === CatalogSearchErrorCode.QueryTooLong
          ? i18n.t('sales_portal.order_entry.catalog_search.query_too_long')
          : i18n.t('sales_portal.order_entry.catalog_search.error');
      showErrorToast(message);
    },
    [showErrorToast],
  );

  // Scroll catalog table to top when search completes
  const handleCatalogSearchComplete = useCallback(() => {
    catalogScrollHandleRef.current?.scrollToTop();
  }, []);

  // Order guide selection
  const [selectedOrderGuide, setSelectedOrderGuide] =
    useState<OrderGuideCollectionData | null>(null);

  const handleOrderGuidesLoaded = useCallback(
    (guides: OrderGuideCollectionData[]) => {
      const defaultGuide = guides.find((g) => g.isDefault) ?? guides[0] ?? null;
      setSelectedOrderGuide(defaultGuide);
    },
    [],
  );

  const { orderGuides } = useSalesPortalOrderGuideCollectionsLoader(
    restaurantUUID,
    supplierUUID,
    handleOrderGuidesLoaded,
  );

  // Defer order guide UUID to avoid blocking input during table re-render.
  // Use the collection's own UUID directly, matching the operator portal which
  // resolves linked supplier collections at the item filtering level.
  const deferredOrderGuideUUID = useDeferredValue(selectedOrderGuide?.uuid);

  // Combined cart + order entry loader (single request; sync runs inside the loader).
  const {
    tableData,
    priceEditDisplayHeaders,
    loading: orderEntryDataLoading,
    error: orderEntryDataError,
    refetch: refetchOrderEntryData,
  } = useOrderEntryDataLoader({
    deliveryDate,
    linkedSupplierCollectionUUID:
      selectedOrderGuide?.linkedSupplierCollectionUUID ?? undefined,
    orderGuideUUID: deferredOrderGuideUUID,
    restaurantUUID,
    salesRepConfig,
    savedSelectedFulfillment,
    supplierTimezone,
    supplierUUID,
  });

  // Catalog search loader
  const { cancelCatalogSearch, executeCatalogSearch } = useCatalogSearchLoader({
    deliveryDate,
    onComplete: handleCatalogSearchComplete,
    onError: handleCatalogSearchError,
    priceEditDisplayHeaders,
    restaurantUUID,
    salesRepConfig,
    supplierUUID,
  });

  // Auto-search: fires as user types while in catalog mode (debounced)
  const debouncedCatalogSearch = useDebouncedCallback((text: string) => {
    if (!isInCatalogMode) {
      return;
    }

    // Clear text exits catalog mode (debounced to avoid flash on quick clear-and-retype)
    if (!text.trim()) {
      resetCatalogSearch();
      return;
    }

    if (text.trim() !== catalogSearchTerm) {
      closeItemDetailPanel();
      void executeCatalogSearch(text.trim());
    }
  }, CATALOG_SEARCH_DEBOUNCE_MS);

  // PERF: Ref-stable callback — executeCatalogSearch has an unstable reference
  // (deep Apollo lazy query dep chain) which cascades to buildEmptyState and
  // defeats React.memo on the OrderEntryTable on every render.
  const executeCatalogSearchRef = useRef(executeCatalogSearch);
  executeCatalogSearchRef.current = executeCatalogSearch;
  // Likewise for performance reasons closeItemDetailPanel is used in the catalog
  // search flow as well and needs to be memoized
  const closeItemDetailPanelRef = useRef(closeItemDetailPanel);
  closeItemDetailPanelRef.current = closeItemDetailPanel;

  // Explicit trigger: fires on Enter key press or empty-state CTA
  const handleCatalogSearch = useCallback((text: string) => {
    if (!text.trim()) {
      return;
    }
    closeItemDetailPanelRef.current();
    void executeCatalogSearchRef.current(text.trim());
  }, []);

  // Composed cleanup: cancel pending debounce, cancel in-flight query, reset store
  const resetCatalogSearch = useCallback(() => {
    debouncedCatalogSearch.cancel();
    cancelCatalogSearch();
    onExitCatalogMode();
  }, [debouncedCatalogSearch, cancelCatalogSearch, onExitCatalogMode]);

  const handleSearchChange = useCallback(
    (text: string) => {
      // Update debounced text for table filtering; clear immediately for responsiveness
      if (!text.trim()) {
        debouncedSetSearchText.cancel();
        setDebouncedSearchText('');
      } else {
        debouncedSetSearchText(text);
      }

      if (!text.trim() && isInCatalogMode) {
        resetCatalogSearch();
      } else {
        debouncedCatalogSearch(text);
      }

      debouncedResetLastFocused();
    },
    [
      debouncedCatalogSearch,
      debouncedResetLastFocused,
      debouncedSetSearchText,
      isInCatalogMode,
      resetCatalogSearch,
      setDebouncedSearchText,
    ],
  );

  // PERF: Clears search without triggering parent re-render from raw text.
  // Immediately resets debounced state so tables update in the same frame.
  const clearSearch = useCallback(() => {
    debouncedSetSearchText.cancel();
    setDebouncedSearchText('');
    searchInputRef.current?.clear();
  }, [debouncedSetSearchText]);

  const handleExitCatalogModeAndClear = useCallback(() => {
    closeItemDetailPanel();
    resetCatalogSearch();
    clearSearch();
    searchInputRef.current?.blur();
  }, [clearSearch, closeItemDetailPanel, resetCatalogSearch]);

  // Reset catalog store when context changes (restaurant/supplier/delivery date)
  useEffect(() => {
    resetCatalogSearch();
  }, [restaurantUUID, supplierUUID, deliveryDate, resetCatalogSearch]);

  // OG switch handler: updates selection + re-triggers catalog search during error state
  const handleSelectOrderGuide = useCallback(
    (guide: OrderGuideCollectionData) => {
      setSelectedOrderGuide(guide);

      if (showCatalogError && catalogSearchTerm) {
        void executeCatalogSearch(catalogSearchTerm);
      }
    },
    [showCatalogError, catalogSearchTerm, executeCatalogSearch],
  );

  const { lastPurchaseInfoMap } = useLastPurchaseInfoLoader(
    selectedRestaurant.chatUUID,
    timezones.restaurant,
  );

  // Refetch when screen regains focus (e.g. browser back), but not on initial load.
  const hasFocusedRef = useRef(false);
  const refetchOrderEntryDataRef = useRef(refetchOrderEntryData);
  refetchOrderEntryDataRef.current = refetchOrderEntryData;
  useFocusEffect(
    useCallback(() => {
      if (hasFocusedRef.current) {
        refetchOrderEntryDataRef.current?.();
      } else {
        hasFocusedRef.current = true;
      }
    }, []),
  );

  // TODO [DSR-3475] remove need for useShallow(); without it infinite loop is triggered
  const cartItems = useCartStore(
    useShallow((state) => selectSalesPortalCartItems(state)),
  );

  const orderUUID = useCartStore(useShallow(selectSalesPortalCartOrderId));

  // Commission: fetch estimated commission when experiment is enabled and order exists
  const { commissionDisplayValue, isCommissionsLoading, refetchCommission } =
    useOrderEntryCommissionLoader({
      authenticatedUserUUID,
      currencyCode,
      enabled: isCommissionsEnabled,
      orderUUID,
    });

  // Stale state: set true immediately when a price/quantity edit begins
  // (before the save mutation), cleared when the commission refetch completes.
  const [isCommissionStale, setIsCommissionStale] = useState(false);

  // Wrap cart save with commission stale/refetch lifecycle
  const saveCartBase = useSaveSalesPortalCartMutator();
  const handleSaveCart = useCallback(async () => {
    setIsCommissionStale(true);
    const success = await saveCartBase();
    if (success) {
      await refetchCommission?.();
    }
    setIsCommissionStale(false);
  }, [refetchCommission, saveCartBase]);

  // Wrap price save to mark stale immediately, then refetch only on success
  const handleSavePriceEdit = useCallback(
    async (...args: Parameters<typeof handleSavePriceEditBase>) => {
      setIsCommissionStale(true);
      const success = await handleSavePriceEditBase(...args);
      if (success) {
        await refetchCommission?.();
      }
      setIsCommissionStale(false);
    },
    [handleSavePriceEditBase, refetchCommission],
  );

  // Get review table data for the cart items
  const { tableData: rawReviewTableData } =
    useSalesPortalOrderEntryReviewDataLoader();

  const onPressBuild = useCallback(() => {
    // PERF: Set mode immediately so the segmented control indicator moves
    // without waiting for React to finish a transition batch.
    // Defer search cleanup to the next idle period.
    setOrderEntryMode(OrderEntryMode.Build);
    closeItemDetailPanel();
    requestIdleCallbackSafe(() => {
      clearSearch();
      resetCatalogSearch();
    });
  }, [clearSearch, closeItemDetailPanel, resetCatalogSearch]);

  const onPressReview = useCallback(() => {
    setOrderEntryMode(OrderEntryMode.Review);
    closeItemDetailPanel();
    requestIdleCallbackSafe(() => {
      clearSearch();
      resetCatalogSearch();
    });
  }, [clearSearch, closeItemDetailPanel, resetCatalogSearch]);

  // PERF: Stabilize callback identity so React.memo on the inactive table isn't
  // defeated when orderEntryMode flips the handler between onPressReview/onPressBuild.
  // Read mode via a ref at call time; onPressBuild/onPressReview are stable callbacks.
  const orderEntryModeRef = useRef(orderEntryMode);
  orderEntryModeRef.current = orderEntryMode;
  const switchBuildReviewModeHandler = useCallback(() => {
    // On native, this handler is only reachable from a focused quantity
    // cell's onKeyPress, so always restore focus. On web, the global
    // keyboard shortcut can also trigger this with no cell focused —
    // the quantity cell's handleKeyPress calls requestFocusRestore()
    // directly for the web case (see order-entry-table-quantity-cell).
    if (Platform.OS !== 'web') {
      useTableFocusStore.getState().requestFocusRestore();
    }
    if (orderEntryModeRef.current === OrderEntryMode.Build) {
      onPressReview();
    } else {
      onPressBuild();
    }
  }, [onPressBuild, onPressReview]);

  // Keyboard shortcut: R to toggle between Build and Review mode
  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.SwitchBuildReviewMode,
    switchBuildReviewModeHandler,
    [switchBuildReviewModeHandler],
    {},
    true,
    SalesPortalKeyboardScope.ORDER_ENTRY_TABLE,
  );

  // TEMPORARY SOLUTION: product wants review items to persist until the tab is changed
  //  will be re-written when we start fetching from a local DB
  // TODO(DSR-3475): update this solution when we review table and cart item memoization
  const [reviewTableData, setReviewTableData] = useState<
    OrderEntryTableRowData[]
  >([]);

  useEffect(() => {
    // In Build mode: full sync so tab change is instant.
    // In Review mode: refresh fields for rows still present in the live data
    // (so edits like price overrides reflect immediately) and append rows
    // newly added to the cart (e.g. cart-only variant-pack siblings landed by
    // the item-detail prefetch after the user switched to Review), while
    // keeping snapshots of rows removed from cart so review items don't
    // disappear mid-review.
    if (orderEntryMode === OrderEntryMode.Build) {
      setReviewTableData(rawReviewTableData ?? []);
      return;
    }
    const liveRows = rawReviewTableData ?? [];
    const liveByKey = new Map(
      liveRows.map((row) => [row.variantPackUUID, row]),
    );
    setReviewTableData((current) => {
      const refreshed = current.map(
        (row) => liveByKey.get(row.variantPackUUID) ?? row,
      );
      const currentKeys = new Set(current.map((row) => row.variantPackUUID));
      const additions = liveRows.filter(
        (row) => !currentKeys.has(row.variantPackUUID),
      );
      return additions.length > 0 ? [...refreshed, ...additions] : refreshed;
    });
  }, [rawReviewTableData, orderEntryMode]);

  // Account on hold state
  const isAccountOnHold = selectedRestaurant.onHold;

  /**
   * Checkout
   * Check action param to determine if checkout panel should be open
   * Never show checkout panel if account is on hold
   */
  const shouldShowCheckoutPanel =
    action === SalesPortalOrderEntryAction.CHECKOUT && !isAccountOnHold;
  const checkoutDrawerRef = useRef<SideDrawerRef>(null);

  const configurableColumns = useMemo(
    () =>
      getOrderEntryConfigurableColumns({
        canWriteRequestNote,
        isFulfilledHidden: !lastPurchaseInfoMap,
        isGrossProfitHidden,
        priceEditDisplayHeaders,
      }),
    [
      isGrossProfitHidden,
      canWriteRequestNote,
      priceEditDisplayHeaders,
      lastPurchaseInfoMap,
    ],
  );

  // Determine if order guide selector should be rendered at all
  const shouldRenderOrderGuideSelector =
    orderGuides?.length > 1 && orderEntryMode === OrderEntryMode.Build;

  // Determine if selector should collapse (catalog mode, except error)
  const shouldCollapseOrderGuideSelector = isInCatalogMode && !showCatalogError;

  const selectorProgress = useDerivedValue(() =>
    withTiming(shouldCollapseOrderGuideSelector ? 1 : 0, {
      ...DEFAULT_EASING_OUT_CONFIG,
    }),
  );

  const selectorAnimatedStyle = useAnimatedStyle(() => {
    const progress = selectorProgress.value;
    return {
      marginLeft: theme.sk.spacing.spaceMedium * (1 - progress),
      opacity: 1 - progress,
      width: SELECTOR_MAX_WIDTH * (1 - progress),
    };
  });

  const onCheckout = useCallback(() => {
    navigation.navigate(SALES_PORTAL_ROUTES.OrderEntry, {
      action: SalesPortalOrderEntryAction.CHECKOUT,
      restaurantUUID,
      supplierUUID,
    });
  }, [navigation, restaurantUUID, supplierUUID]);

  // Generic handlers for disabling/enabling ORDER_ENTRY_TABLE scope
  const handleDisableOrderEntryTableScope = useCallback(() => {
    disableScope(SalesPortalKeyboardScope.ORDER_ENTRY_TABLE);
  }, [disableScope]);

  const handleEnableOrderEntryTableScope = useCallback(() => {
    enableScope(SalesPortalKeyboardScope.ORDER_ENTRY_TABLE);
  }, [enableScope]);

  const onOrderPlaced = useCallback(() => {
    setReviewTableData([]);
  }, []);

  const onOpenCheckoutPanel = useCallback(() => {
    handleDisableOrderEntryTableScope();
  }, [handleDisableOrderEntryTableScope]);

  const closeIdleCallbackRef = useRef<number | undefined>(undefined);

  // Cancel pending idle callback on unmount
  useEffect(() => {
    return () => {
      cancelIdleCallbackSafe(closeIdleCallbackRef.current);
    };
  }, []);

  const onCloseCheckoutPanel = useCallback(() => {
    // PERF: Start the dismiss animation immediately on the UI thread,
    // then defer the heavy navigation update to the next idle period.
    checkoutDrawerRef.current?.dismiss();
    closeIdleCallbackRef.current = requestIdleCallbackSafe(() => {
      handleEnableOrderEntryTableScope();
      navigation.navigate(SALES_PORTAL_ROUTES.OrderEntry, {
        restaurantUUID,
        supplierUUID,
      });
    });
  }, [
    navigation,
    restaurantUUID,
    supplierUUID,
    handleEnableOrderEntryTableScope,
  ]);

  // PERF: Ref-stable toggle so the table's onToggleCheckout prop doesn't
  // change reference when shouldShowCheckoutPanel flips, defeating React.memo.
  const _handleToggleCheckout = shouldShowCheckoutPanel
    ? onCloseCheckoutPanel
    : onCheckout;
  const handleToggleCheckoutRef = useRef(_handleToggleCheckout);
  handleToggleCheckoutRef.current = _handleToggleCheckout;

  const handleToggleCheckout = useCallback(() => {
    handleToggleCheckoutRef.current();
  }, []);

  // Clean up URL param if on hold (panel already blocked via shouldShowCheckoutPanel)
  useEffect(() => {
    if (isAccountOnHold && action === SalesPortalOrderEntryAction.CHECKOUT) {
      navigation.navigate(SALES_PORTAL_ROUTES.OrderEntry, {
        restaurantUUID,
        supplierUUID,
      });
    }
  }, [isAccountOnHold, action, navigation, restaurantUUID, supplierUUID]);

  // Sync keyboard scope with panel visibility so shortcuts (C, Shift+Enter) work when
  // panel is open without requiring focus to move into the Modal.
  useEffect(() => {
    if (shouldShowCheckoutPanel) {
      disableScope(SalesPortalKeyboardScope.ORDER_ENTRY_TABLE);
    } else {
      enableScope(SalesPortalKeyboardScope.ORDER_ENTRY_TABLE);
    }
  }, [shouldShowCheckoutPanel, disableScope, enableScope]);

  // Keyboard shortcut: C to toggle checkout panel (disabled when account is on hold)
  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.ToggleCheckout,
    handleToggleCheckout,
    [handleToggleCheckout],
    {},
    !isAccountOnHold && (cartItems.length > 0 || shouldShowCheckoutPanel),
    SalesPortalKeyboardScope.CHECKOUT_PANEL,
  );

  const tableBodyContainerStyle = useMemo(
    () =>
      bottomBarHeight > 0
        ? { paddingBottom: bottomBarHeight + theme.sk.spacing.spaceLarge }
        : undefined,
    [bottomBarHeight, theme.sk.spacing.spaceLarge],
  );

  // Memoize container styles to prevent unnecessary re-renders
  // Hide OG tables when in catalog mode
  const mainTableContainerStyle = useMemo(
    () =>
      StyleSheet.flatten([
        styles.tableContainerStyle,
        (deferredOrderEntryMode === OrderEntryMode.Review || isInCatalogMode) &&
          styles.hidden,
      ]),
    [
      deferredOrderEntryMode,
      isInCatalogMode,
      styles.hidden,
      styles.tableContainerStyle,
    ],
  );

  const reviewTableContainerStyle = useMemo(
    () =>
      StyleSheet.flatten([
        styles.tableContainerStyle,
        (deferredOrderEntryMode === OrderEntryMode.Build || isInCatalogMode) &&
          styles.hidden,
      ]),
    [
      deferredOrderEntryMode,
      isInCatalogMode,
      styles.hidden,
      styles.tableContainerStyle,
    ],
  );

  const catalogTableContainerStyle = useMemo(
    () =>
      StyleSheet.flatten([
        styles.tableContainerStyle,
        !showCatalogResults && !showCatalogEmptyState && styles.hidden,
      ]),
    [
      showCatalogEmptyState,
      showCatalogResults,
      styles.hidden,
      styles.tableContainerStyle,
    ],
  );

  // Catalog empty state: redo search clears text, exits catalog mode, and focuses input
  const handleRedoCatalogSearch = useCallback(() => {
    resetCatalogSearch();
    clearSearch();
    searchInputRef.current?.focus();
  }, [clearSearch, resetCatalogSearch]);

  // Catalog loading skeleton shown while search is in flight
  const CatalogLoadingSkeleton = useMemo(() => {
    if (!isCatalogLoading) {
      return null;
    }
    return <OrderEntryTableSkeleton />;
  }, [isCatalogLoading]);

  const CatalogEmptyState = useMemo(
    () => (
      <CatalogSearchEmptyState
        actionLabel={i18n.t('sales_portal.order_entry.catalog_empty.redo')}
        onAction={handleRedoCatalogSearch}
        title={i18n.t('sales_portal.order_entry.catalog_empty.title', {
          query: catalogSearchTerm,
        })}
      />
    ),
    [handleRedoCatalogSearch, catalogSearchTerm],
  );

  // PERF: Split empty states per mode so toggling doesn't invalidate the
  // inactive table's emptyState prop (which would defeat React.memo).
  const buildEmptyState = useMemo(() => {
    if (debouncedSearchText) {
      return (
        <OrderEntrySearchEmptyState
          onSearchCatalog={() => handleCatalogSearch(debouncedSearchText)}
          searchTerm={debouncedSearchText}
        />
      );
    }
    return <OrderEntryEmptyState />;
  }, [debouncedSearchText, handleCatalogSearch]);

  const reviewEmptyState = useMemo(() => {
    if (debouncedSearchText) {
      return (
        <OrderEntryReviewSearchEmptyState searchTerm={debouncedSearchText} />
      );
    }
    return <OrderEntryReviewEmptyState />;
  }, [debouncedSearchText]);

  // PERF: Return EMPTY_TABLE_STATE (stable ref) for the inactive mode so
  // React.memo on the inactive table isn't defeated by a new {} each debounce.
  const buildTableState = useMemo((): Partial<TableState> => {
    return deferredOrderEntryMode === OrderEntryMode.Build
      ? { globalFilter: debouncedSearchText }
      : EMPTY_TABLE_STATE;
  }, [deferredOrderEntryMode, debouncedSearchText]);

  const reviewTableState = useMemo((): Partial<TableState> => {
    return deferredOrderEntryMode === OrderEntryMode.Review
      ? { globalFilter: debouncedSearchText }
      : EMPTY_TABLE_STATE;
  }, [deferredOrderEntryMode, debouncedSearchText]);

  // Footer shown at end of OG search results prompting user to search catalog
  const searchCatalogFooter = useMemo(() => {
    // Only show when searching in Build mode (not catalog mode)
    if (
      orderEntryMode !== OrderEntryMode.Build ||
      isInCatalogMode ||
      !debouncedSearchText
    ) {
      return undefined;
    }

    return (
      <SearchCatalogFooter
        onPress={() => handleCatalogSearch(debouncedSearchText)}
      />
    );
  }, [
    orderEntryMode,
    isInCatalogMode,
    debouncedSearchText,
    handleCatalogSearch,
  ]);

  // loading state - wait for combined data to load (including on refetch e.g. browser back) and order guide to be selected
  const isLoading = orderEntryDataLoading || !selectedOrderGuide;

  // error state (combined cart + order entry load)
  if (orderEntryDataError) {
    return (
      <ErrorState
        ctaProps={{ disabled: orderEntryDataLoading }}
        description={i18n.t('sales_portal.order_entry.error.table.description')}
        onPress={() => refetchOrderEntryData()}
        title={i18n.t('sales_portal.order_entry.error.table.title')}
      />
    );
  }

  return (
    <>
      {isLoading || !tableData ? (
        <View style={styles.loadingContent}>
          <View style={styles.loadingSearchContainer}>
            <AnimatedSkeletonLoader
              borderRadius={8}
              endOpacity={0.15}
              height={38}
              initialOpacity={0.3}
              width={556}
            />
          </View>
          <OrderEntryTableSkeleton />
        </View>
      ) : (
        <View
          style={[
            styles.contentContainer,
            Platform.OS !== 'web' && styles.contentContainerNative,
          ]}
        >
          <View style={styles.searchAndGuideContainer}>
            <View style={styles.searchInputWrapper}>
              <OrderEntrySearchInput
                isInCatalogMode={isInCatalogMode}
                isLoading={isCatalogLoading}
                onCatalogSearch={handleCatalogSearch}
                onClearAndExit={handleExitCatalogModeAndClear}
                onEscapePress={handleExitCatalogModeAndClear}
                onFocus={handleSearchFocus}
                onKeyPress={handleSearchKeyPress}
                onSearchChange={handleSearchChange}
                onTabForward={handleSearchDownArrow}
                ref={searchInputRef}
              />
            </View>
            {shouldRenderOrderGuideSelector ? (
              <Animated.View
                style={[styles.selectorWrapper, selectorAnimatedStyle]}
              >
                <OrderGuideSelector
                  listData={orderGuides}
                  onSelectItem={handleSelectOrderGuide}
                  selectedItem={selectedOrderGuide}
                />
              </Animated.View>
            ) : null}
            <ConfigureColumnsButton
              columns={configurableColumns}
              domain={ConfigurationDomain.Ordering}
              table={TableName.OrderEntry}
            />
          </View>

          <View
            style={[
              styles.tableWrapper,
              Platform.OS !== 'web' && styles.tableWrapperNative,
            ]}
          >
            <OrderEntryTable
              areSubstitutionsEnabled={areSubstitutionsEnabled}
              bodyContainerStyle={tableBodyContainerStyle}
              canSetManualOverrideExpiration={canSetManualOverrideExpiration}
              canWriteRequestNote={canWriteRequestNote}
              containerStyle={mainTableContainerStyle}
              // PERF: Both tables are always mounted with opacity:0 when hidden,
              // so FlashList renders rows regardless. Keeping data stable avoids
              // a full TanStack row model rebuild on every mode switch.
              data={tableData}
              emptyState={buildEmptyState}
              isActive={
                deferredOrderEntryMode === OrderEntryMode.Build &&
                !isInCatalogMode
              }
              isGrossProfitHidden={isGrossProfitHidden}
              isPriceEditingEnabled={isPriceEditingEnabled}
              lastPurchaseInfoMap={lastPurchaseInfoMap}
              listFooter={searchCatalogFooter}
              minMaxQuantityEnabled={minMaxQuantityEnabled}
              onFocusedRowChange={onFocusedRowChange}
              onOpenRestaurantSelector={onOpenRestaurantSelector}
              onPriceValidationError={showErrorToast}
              onRowPress={onRowPress}
              onSaveCart={handleSaveCart}
              onSavePriceEdit={handleSavePriceEdit}
              onSaveRequestNote={handleSaveRequestNote}
              onSwitchBuildReviewMode={switchBuildReviewModeHandler}
              onToggleCheckout={handleToggleCheckout}
              onTriggerSearch={handleTriggerSearch}
              priceEditDisplayHeaders={priceEditDisplayHeaders}
              quantityInputDisabled={isAccountOnHold}
              selectedCollectionId={selectedOrderGuide?.uuid}
              showPriceOverrideInfo={showPriceOverrideInfo}
              tableState={buildTableState}
            />

            {/* Overlayed Review Table - only shown in review mode, done for performance reasons */}
            <OrderEntryTable
              areSubstitutionsEnabled={areSubstitutionsEnabled}
              bodyContainerStyle={tableBodyContainerStyle}
              canSetManualOverrideExpiration={canSetManualOverrideExpiration}
              canWriteRequestNote={canWriteRequestNote}
              containerStyle={reviewTableContainerStyle}
              data={reviewTableData}
              emptyState={reviewEmptyState}
              initialSorting={REVIEW_DEFAULT_SORTING}
              isActive={
                deferredOrderEntryMode === OrderEntryMode.Review &&
                !isInCatalogMode
              }
              isGrossProfitHidden={isGrossProfitHidden}
              isPriceEditingEnabled={isPriceEditingEnabled}
              lastPurchaseInfoMap={lastPurchaseInfoMap}
              minMaxQuantityEnabled={minMaxQuantityEnabled}
              onFocusedRowChange={onFocusedRowChange}
              onOpenRestaurantSelector={onOpenRestaurantSelector}
              onPriceValidationError={showErrorToast}
              onRowPress={onRowPress}
              onSaveCart={handleSaveCart}
              onSavePriceEdit={handleSavePriceEdit}
              onSaveRequestNote={handleSaveRequestNote}
              onSwitchBuildReviewMode={switchBuildReviewModeHandler}
              onToggleCheckout={handleToggleCheckout}
              onTriggerSearch={handleTriggerSearch}
              priceEditDisplayHeaders={priceEditDisplayHeaders}
              quantityInputDisabled={isAccountOnHold}
              selectedCollectionId={selectedOrderGuide?.uuid}
              showPriceOverrideInfo={showPriceOverrideInfo}
              tableState={reviewTableState}
            />
            <CatalogSearchTable
              areSubstitutionsEnabled={areSubstitutionsEnabled}
              bodyContainerStyle={tableBodyContainerStyle}
              containerStyle={catalogTableContainerStyle}
              data={catalogResults}
              emptyState={CatalogEmptyState}
              isActive={showCatalogResults || showCatalogEmptyState}
              isGrossProfitHidden={isGrossProfitHidden}
              isPriceEditingEnabled={isPriceEditingEnabled}
              lastPurchaseInfoMap={lastPurchaseInfoMap}
              minMaxQuantityEnabled={minMaxQuantityEnabled}
              onFocusedRowChange={onFocusedRowChange}
              onOpenRestaurantSelector={onOpenRestaurantSelector}
              onPriceValidationError={showErrorToast}
              onRowPress={onRowPress}
              onSaveCart={handleSaveCart}
              onSavePriceEdit={handleSavePriceEdit}
              onSwitchBuildReviewMode={switchBuildReviewModeHandler}
              onToggleCheckout={handleToggleCheckout}
              onTriggerSearch={handleTriggerSearch}
              priceEditDisplayHeaders={priceEditDisplayHeaders}
              quantityInputDisabled={isAccountOnHold}
              scrollHandleRef={catalogScrollHandleRef}
              searchQuery={catalogSearchTerm}
            />
            {CatalogLoadingSkeleton}
          </View>
        </View>
      )}

      {!isAccountOnHold && !isLoading && tableData ? (
        <OrderSummaryBottomBar
          commissionDisplayValue={commissionDisplayValue}
          currencyCode={currencyCode}
          hasItemsInCart={cartItems.length > 0}
          isCommissionsEnabled={isCommissionsEnabled}
          isCommissionsLoading={isCommissionsLoading || isCommissionStale}
          isGrossProfitHidden={isGrossProfitHidden}
          onBuild={onPressBuild}
          onCheckout={onCheckout}
          onLayout={onBottomBarLayout}
          onReview={onPressReview}
          orderEntryMode={orderEntryMode}
        />
      ) : null}

      <CheckoutPanel
        additionalOrderValidationRoles={additionalOrderValidationRoles}
        authenticatedUserUUID={authenticatedUserUUID}
        availabilityConstraints={availabilityConstraints}
        drawerRef={checkoutDrawerRef}
        employeeRole={employeeRole}
        fulfillment={{
          availableFulfillments,
          getTimeslotOptionsForDate,
          onRefetch: refetchFulfillments,
          savedSelectedFulfillment,
          saveSelectedFulfillment: handleSelectedFulfillment,
          selectableFulfillmentOptions,
          timezones,
        }}
        isCommissionsEnabled={isCommissionsEnabled}
        isGrossProfitHidden={isGrossProfitHidden}
        isOpen={shouldShowCheckoutPanel}
        onClose={onCloseCheckoutPanel}
        onOpen={onOpenCheckoutPanel}
        onOrderPlaced={onOrderPlaced}
        restaurantUUID={restaurantUUID}
        selectedRestaurant={selectedRestaurant}
        supplierUUID={supplierUUID}
      />
    </>
  );
};

const useStyles = createStyles((theme) => ({
  contentContainer: {
    flex: 1,
    gap: theme.sk.spacing.spaceMedium,
  },
  /** On native, avoid gap when container has absolutely positioned descendants (table).
   * Yoga incorrectly adds gutters for abs-positioned children, which can overlap the search.
   * Use marginTop on tableWrapper instead. See facebook/yoga#1652. */
  contentContainerNative: {
    gap: 0,
  },
  hidden: {
    // use opacity instead of display: 'none' to prevent onLayout rendering delay in OrderEntryTable
    opacity: 0,
    pointerEvents: 'none',
  },
  loadingContent: {
    flex: 1,
    gap: 42,
  },
  loadingSearchContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchAndGuideContainer: {
    alignItems: 'stretch',
    alignSelf: 'center',
    flexDirection: 'row',
    flexShrink: 0,
    maxWidth: 692,
    // Height of the search-input atom and dropdown menu trigger
    minHeight: 42,
    minWidth: 556,
    width: '50%',
  },
  searchInputWrapper: {
    flex: 1,
  },
  selectorWrapper: {
    overflow: 'hidden',
  },
  tableContainerStyle: {
    backgroundColor: theme.colors.white,
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  tableWrapper: {
    flex: 1,
    marginLeft: theme.sk.spacing.spaceLarge,
    pointerEvents: 'box-none',
    position: 'relative',
  },
  /** On native, spacing between search and table (replaces gap to avoid Yoga gap+absolute bug). */
  tableWrapperNative: {
    marginTop: theme.sk.spacing.spaceMedium,
  },
}));
